import { MobilePhone } from "@kitchen/schemas";
import { Button, ConnectionBar, Field, Icon, PinPad, Seg } from "@kitchen/ui";
import { useEffect, useState } from "react";
import { api, ApiError, type Enrolment } from "../api.js";
import { useHeartbeat } from "../heartbeat.js";
import { type DeviceCreds, loadDevice, saveDevice, type SessionInfo } from "../session.js";

type Door = "customer" | "staff";
type Step =
  | { kind: "door" }
  | { kind: "phone"; door: Door; purpose: "enroll" | "reverify" }
  | { kind: "otp"; door: Door; phone: string; challengeId: string; purpose: "enroll" | "reverify" }
  | { kind: "set-pin"; first?: string }
  | { kind: "pin" }
  | { kind: "pending" };

const platform = (): "web" | "android" | "ios-pwa" =>
  /iphone|ipad/i.test(navigator.userAgent) ? "ios-pwa" : "web";

/** Sign-in (ADR-29, design/v2 Login.html): two doors, SMS once per device, the person's own PIN every time. */
export function Login({ onSignedIn }: { onSignedIn: (s: SessionInfo, device: DeviceCreds) => void }) {
  const hb = useHeartbeat();
  const [device, setDevice] = useState<DeviceCreds | null>(() => loadDevice());
  const [step, setStep] = useState<Step>(() => (loadDevice() ? { kind: "pin" } : { kind: "door" }));
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [context, setContext] = useState<Door>(() => loadDevice()?.door ?? "customer");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState("");

  const fail = (err: unknown) => {
    if (!(err instanceof ApiError)) {
      setError("ارتباط با سرور برقرار نشد");
      return;
    }
    if (err.code === "needs-reverify") {
      setNotice(err.message);
      setStep({ kind: "phone", door: device?.door ?? "customer", purpose: "reverify" });
      return;
    }
    if (err.code === "device-unknown" || err.code === "device-revoked") {
      saveDevice(null);
      setDevice(null);
      setStep({ kind: "door" });
    }
    if (err.code === "device-pending-approval") setStep({ kind: "pending" });
    // The server knows whether this person already chose a PIN; follow it instead of guessing.
    if (err.code === "pin-not-set") {
      setStep({ kind: "set-pin" });
      return;
    }
    if (err.code === "pin-already-set") {
      setStep({ kind: "pin" });
      return;
    }
    setError(err.message);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const requestCode = (door: Door, purpose: "enroll" | "reverify") =>
    run(async () => {
      const parsed = MobilePhone.safeParse(phone);
      if (!parsed.success) throw new ApiError(400, "invalid", "شماره‌ی موبایل معتبر نیست");
      const res = await api<{ kind: string; challengeId?: string; message?: string }>("/auth/otp/request", {
        body: {
          door,
          phone: parsed.data,
          purpose,
          deviceLabel: navigator.platform || "مرورگر",
          platform: platform(),
        },
      });
      if (res.kind === "not-on-staff-list") {
        setError(res.message ?? "این شماره در فهرست کارکنان نیست");
        return;
      }
      setCode("");
      setStep({ kind: "otp", door, phone: parsed.data, challengeId: res.challengeId ?? "", purpose });
    });

  const verify = (s: Extract<Step, { kind: "otp" }>) =>
    run(async () => {
      const reverify =
        s.purpose === "reverify" && device
          ? { deviceId: device.deviceId, deviceSecret: device.deviceSecret }
          : {};
      const e = await api<Enrolment>("/auth/otp/verify", {
        body: { challengeId: s.challengeId, code, ...reverify },
      });
      const creds: DeviceCreds = {
        deviceId: e.deviceId,
        deviceSecret: e.deviceSecret,
        door: s.door,
        displayName: e.displayName,
        pinLength: e.pinLength,
      };
      saveDevice(creds);
      setDevice(creds);
      setContext(s.door);
      setPin("");
      setNotice(null);
      setStep(
        e.deviceStatus === "pending-approval"
          ? { kind: "pending" }
          : e.needsPin
            ? { kind: "set-pin" }
            : { kind: "pin" },
      );
    });

  // Submit automatically when the PIN is complete: no extra button for staff (AGENTS.md §2: minimal UX).
  useEffect(() => {
    if (!device || pin.length !== device.pinLength || busy) return;
    if (step.kind === "set-pin") {
      if (!step.first) {
        setStep({ kind: "set-pin", first: pin });
        setPin("");
        return;
      }
      if (step.first !== pin) {
        setError("دو PIN یکی نبودند؛ دوباره بسازید");
        setStep({ kind: "set-pin" });
        setPin("");
        return;
      }
      void run(async () => {
        await api("/auth/pin", {
          body: { deviceId: device.deviceId, deviceSecret: device.deviceSecret, pin },
        });
        const s = await api<{ token: string }>("/auth/unlock", {
          body: { deviceId: device.deviceId, deviceSecret: device.deviceSecret, pin, context },
        });
        onSignedIn({ token: s.token, context }, device);
      }).finally(() => {
        setPin("");
      });
    }
    if (step.kind === "pin") {
      void run(async () => {
        const s = await api<{ token: string }>("/auth/unlock", {
          body: { deviceId: device.deviceId, deviceSecret: device.deviceSecret, pin, context },
        });
        onSignedIn({ token: s.token, context }, device);
      }).finally(() => {
        setPin("");
      });
    }
  }, [pin]);

  return (
    <main className="login-page">
      <section className="panel login-box" aria-labelledby="login-title">
        <div className="row-between">
          <span className="brand-mark" aria-hidden="true">
            <Icon name="chef" />
          </span>
          <ConnectionBar state={hb.state} lastOkAt={hb.lastOkAt} now={hb.now} />
        </div>
        <div>
          <h1 id="login-title" className="t-page">
            ورود
          </h1>
          <p className="muted">آشپزخانه‌ی مرکزی</p>
        </div>

        {notice && <p className="hint">{notice}</p>}

        {step.kind === "door" && (
          <div className="stack">
            <Button
              variant="primary"
              size="lg"
              block
              icon="building"
              onClick={() => {
                setStep({ kind: "phone", door: "customer", purpose: "enroll" });
              }}
            >
              ورود مشتری و رابط سفارش
            </Button>
            <Button
              size="lg"
              block
              icon="chef"
              onClick={() => {
                setStep({ kind: "phone", door: "staff", purpose: "enroll" });
              }}
            >
              ورود کارکنان
            </Button>
          </div>
        )}

        {step.kind === "phone" && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void requestCode(step.door, step.purpose);
            }}
          >
            <p className="ink2">
              {step.door === "staff" ? "ورود کارکنان" : "ورود مشتری"}: یک کد پیامکی برای همین دستگاه
              می‌فرستیم.
            </p>
            <Field
              id="phone"
              label="شماره‌ی موبایل"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
              }}
              placeholder="۰۹۱۲ ۰۰۰ ۰۰۰۰"
              error={error}
            />
            <Button type="submit" variant="primary" size="lg" block disabled={busy}>
              ارسال کد
            </Button>
            {!device && (
              <Button
                variant="ghost"
                onClick={() => {
                  setError(null);
                  setStep({ kind: "door" });
                }}
              >
                برگشت
              </Button>
            )}
          </form>
        )}

        {step.kind === "otp" && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void verify(step);
            }}
          >
            <Field
              id="otp"
              label="کد شش‌رقمی پیامک"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              error={error}
            />
            <Button type="submit" variant="primary" size="lg" block disabled={busy || code.trim().length < 6}>
              تأیید
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setStep({ kind: "phone", door: step.door, purpose: step.purpose });
              }}
            >
              شماره را عوض کنید یا کد تازه بخواهید
            </Button>
          </form>
        )}

        {step.kind === "pending" && device && (
          <div className="stack">
            <div className="alert important" role="status">
              <Icon name="shield" />
              <div>
                <div className="b">این دستگاه منتظر تأیید مدیر ارشد است</div>
                <p>پس از تأیید، با PIN خود وارد شوید.</p>
              </div>
              <div className="actions">
                <Button
                  onClick={() => {
                    setStep({ kind: "pin" });
                  }}
                >
                  تأیید شد
                </Button>
              </div>
            </div>
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => {
                  await api("/auth/recovery", {
                    body: { deviceId: device.deviceId, deviceSecret: device.deviceSecret, code: recovery },
                  });
                  setRecovery("");
                  setStep({ kind: "set-pin" });
                });
              }}
            >
              <Field
                id="recovery"
                label="کد نجات چاپی (فقط مدیر ارشد، وقتی کسی برای تأیید نیست)"
                dir="ltr"
                autoComplete="off"
                value={recovery}
                onChange={(e) => {
                  setRecovery(e.target.value);
                }}
                error={error}
              />
              <Button type="submit" disabled={busy || recovery.trim().length < 10}>
                فعال کردن با کد نجات
              </Button>
            </form>
          </div>
        )}

        {(step.kind === "pin" || step.kind === "set-pin") && device && (
          <div className="stack">
            <div className="who">
              <span className="ini b">{device.displayName.slice(0, 2)}</span>
              <span style={{ flex: 1 }}>
                <span className="m">{device.displayName}</span>
                <br />
                <span className="t-cap muted">
                  {step.kind === "set-pin"
                    ? step.first
                      ? "PIN را دوباره بزنید"
                      : `یک PIN ${device.pinLength === 6 ? "شش" : "چهار"}رقمی برای خودتان بسازید`
                    : "PIN خود را بزنید"}
                </span>
              </span>
            </div>
            {device.door === "staff" && step.kind === "pin" && (
              <Seg
                label="ورود به‌عنوان"
                value={context}
                onChange={setContext}
                options={[
                  { value: "staff", label: "کارکنان" },
                  { value: "customer", label: "مشتری" },
                ]}
              />
            )}
            <PinPad length={device.pinLength} value={pin} onChange={setPin} disabled={busy} />
            {error && (
              <p className="hint" role="alert" style={{ color: "var(--coral-ink)", textAlign: "center" }}>
                {error}
              </p>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                saveDevice(null);
                setDevice(null);
                setError(null);
                setStep({ kind: "door" });
              }}
            >
              کاربر دیگری هستید؟
            </Button>
          </div>
        )}
      </section>
    </main>
  );
}
